import { Component, OnInit, ViewChild } from '@angular/core';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { ProfileService } from '@services/profiles/profile.service';
import { GroupHasProfileService } from '@services/groups/group-has-profile.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { ApiKeyService } from '@services/api-keys/api-key.service';
import { CredentialsService } from '@services/credentials/credentials.service';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { DialogService } from '@services/dialog/dialog.service';
import { DataTableComponent } from '../../../components/data-table/data-table.component';
import { GroupSelectionComponent } from '../../../components/popUpSelects/group-selection/group-selection.component';

@Component({
    selector: 'app-profile-detail',
    templateUrl: './profile-detail.component.html',
    styleUrls: ['./profile-detail.component.scss'],
    providers: [ProfileService, GroupHasProfileService, ApiKeyService, CredentialsService],
    standalone: false
})
export class ProfileDetailComponent implements OnInit {

  @ViewChild(DataTableComponent) groupTable: DataTableComponent;

  pageActions = [];
  title: string;
  boundProfile: Models.Profile;
  personalGroupId: number;
  selectedProfileId: string;
  apiKey: string;

  objectKeys = Object.keys;

  isLoaded = false;

  isIbexfusion = false;
  isActivated = false;
  groupColumns = [{ prop: 'groupName', valueTransformer: (val) => (val || '').startsWith('pref_') ? val.split('_').splice(2, val.split('_').length - 1).join('_') : val }];
  groupParams = {};

  constructor(
    private router: Router, private route: ActivatedRoute, private profileService: ProfileService, public groupHasProfileService: GroupHasProfileService,
    private apiKeyService: ApiKeyService, private ms: MessengerService, private credentialsService: CredentialsService, private snackbar: SnackbarService,
    private dialogService: DialogService, private dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.selectedProfileId = this.route.snapshot.params['id'];
    this.groupParams = {
      ProfileID: this.selectedProfileId
    };
    this.profileService.get(this.selectedProfileId).then(result => {
      if (!result) {
        console.error('no profile found');
        throw new Error('no profile found');
      }
      this.boundProfile = new Models.Profile();
      this.boundProfile.populateFromJSON(result);
      this.loadApiKey();
      this.title = this.boundProfile.firstName + ' ' + this.boundProfile.lastName;
      if (this.boundProfile.nickName) {
        this.title = this.boundProfile.nickName;
      }
      this.isLoaded = true;
      this.isIbexfusion = this.boundProfile.userType === 'ibexfusion';
      this.credentialsService.get(this.boundProfile.ProfileID).then(hasValidCredentials => {
        if (hasValidCredentials && hasValidCredentials.length) {
          this.isActivated = true;
        }
        this.createPageActions();
      });
    });
  }

  addToGroup() {
    this.groupHasProfileService.find(0, 100, { ProfileID: this.selectedProfileId }).then(assignedGroups => {
      const dialogRef = this.dialog.open(GroupSelectionComponent, {
        data: {
          ProfileID: this.selectedProfileId,
          assignedGroupIds: assignedGroups.data && assignedGroups.data.length ? assignedGroups.data.map(item => item.GroupID) : []
        },
        width: '800px'
      });

      dialogRef.afterClosed().subscribe(result => {
        if (!result) {
          return;
        }
        const data = new Models.GroupHasProfile();
        data.populateFromJSON({
          ID: null,
          ProfileID: this.selectedProfileId,
          GroupID: result.selectedGroupId
        });
        this.groupHasProfileService.create(data).then(newGroupHasProfile => {
          if (!newGroupHasProfile || !newGroupHasProfile.ProfileID) {
            this.snackbar.error('Could not add Profile');
            return;
          }
          this.snackbar.success('group has been added');
          this.groupTable.refresh();
        });
      });
    });

  }

  createPageActions() {
    this.pageActions = [];
    this.pageActions.push(
      new PageAction({
        icon: 'edit',
        title: 'Edit Profile',
        callback: () => {
          this.router.navigate(['profiles', this.selectedProfileId, 'edit']);
        }
      }),
      new PageAction({
        icon: 'create',
        title: 'Create Profile',
        callback: () => {
          this.router.navigate(['profiles/create']);
        }
      }),
      new PageAction({
        hidden: !this.isActivated || !this.isIbexfusion,
        icon: 'email',
        id: 'forgot_pw',
        title: 'forgot password',
        callback: () => {
          this.sendPasswordReset();
        }
      }),
      new PageAction({
        hidden: this.isActivated || !this.isIbexfusion,
        icon: 'email',
        id: 'activate',
        title: 'activate account',
        callback: () => {
          this.activateAccount();
        }
      }),
      new PageAction({
        hidden: !this.isActivated || !this.isIbexfusion,
        icon: 'add',
        id: 'add_to_group',
        title: 'add to group',
        callback: () => {
          this.addToGroup();
        }
      }),
      new PageAction({
        hidden: !!this.apiKey,
        icon: 'vpn_key',
        id: 'vpn_key',
        title: 'Create API Key',
        callback: () => {
          this.createApiKey();
        }
      })
    );

    this.groupHasProfileService.find(0, 1, { ProfileID: this.selectedProfileId, groupIsPersonal: 1 }).then(result => {
      if (result && result.data && result.data.length === 1) {
        this.personalGroupId = result.data[0]['GroupID'];

        this.pageActions.push(new PageAction({
          icon: 'fingerprint',
          title: 'Manage Roles',
          callback: () => {
            this.router.navigate(['groups', this.personalGroupId, 'roles']);
          }
        }));
      } else {
        this.pageActions.push(new PageAction({
          icon: 'person_add',
          title: 'Create Personal Group',
          callback: () => {
            this.ms.createPersonalGroup(this.selectedProfileId).then(createResult => {
              if (createResult && createResult.data && createResult.data.GroupID) {
                this.createPageActions();
              }
            });
          }
        }));
      }
    });
  }

  updatePageAction(id: string, hidden: boolean, disabled: boolean) {
    this.pageActions.map(pageAction => {
      if (pageAction.options.id === id) {
        pageAction.options.hidden = hidden;
        pageAction.options.disabled = disabled;
      }
    });
  }

  loadApiKey() {
    this.apiKeyService.find({ loginName: this.boundProfile.ProfileID }).then(apiKeyResult => {
      this.apiKey = apiKeyResult;
    });
  }

  createApiKey() {
    this.apiKeyService.create({ loginName: this.boundProfile.ProfileID }).then(result => {
      this.loadApiKey();
      this.snackbar.success('API Key has been created: ' + result, 5000);
    }, err => {
      this.snackbar.error('Error on creating API key: ' + err, 5000);
    });
  }

  sendPasswordReset() {
    if (!this.boundProfile.email) {
      this.snackbar.error('Error: profile has no email');
    }
    this.ms.createResetCredentials(this.boundProfile.email).then(() => {
      this.snackbar.success('successful send forgot email message');
    });
  }

  activateAccount() {
    if (!this.boundProfile.email) {
      this.snackbar.error('Error: Can not activate account without email');
      return;
    }
    const apiKey = 'lXvM6ebAR6|gm0GRcxp20p4zG9nvir2Fxqbv3d6fhRRGNRXo9b9w6eF/Ht+WafSPJRgeAwklPivvUxqleBXPAkgIn8+2z2EIw==';
    this.ms.activateAccount(this.boundProfile, apiKey).then(response => {
      console.log('account has been activated ', response);
      this.updatePageAction('activate', true, false);
      this.updatePageAction('forgot_pw', false, false);
      this.snackbar.success('account has been activated, and activation email send.', 5000);
    }, err => {
      console.log('error on activate account: ', err);
      this.snackbar.error('error on activate account: ' + err, 5000);
    });
  }

  onGroupDetail(event) {
    this.router.navigate(['groups', event.row.GroupID]);
  };

  onGroupRemove(event) {
    this.dialogService
      .confirm('Remove profile from Group')
      .then(res => {
        if (!res) {
          return;
        }
        return this.groupHasProfileService.remove(event.row.ID).then(() => {
          this.groupTable.refresh();
          this.snackbar.success('group has been removed', 3000);
        }).catch(error => {
          const message = (error.error.message ? error.error.message : error.message);
          return this.dialogService.confirm(message, error.name);
        });
      });
  };

}
